/**
 * Color utilities for ODA entities and layers.
 */

import type { DrawingModule } from "../types/DrawingWeb";
import type { DrawingModuleType } from "../DrawingSingleton";
import { deleteObj } from "../core/delete";
import { isDarkBackground } from "../overlay/OverlayTheme";

/**
 * Color source information.
 */
export interface ColorInfo {
  method: string;
  colorIndex?: number;
  rgb?: { r: number; g: number; b: number };
}

/**
 * Get color information from an OdCmColor object.
 * @param colorObj The ODA color object
 * @returns Color information including method and value
 */
export function getColorInfo(colorObj: DrawingModule.OdCmColor): ColorInfo {
  let method: string;
  let colorIndex: number | undefined;
  let rgb: { r: number; g: number; b: number } | undefined;

  if (colorObj.isByLayer()) {
    method = "ByLayer";
  } else if (colorObj.isByBlock()) {
    method = "ByBlock";
  } else if (colorObj.isByACI()) {
    method = "ByACI (palette)";
    colorIndex = colorObj.colorIndex();
  } else if (colorObj.isByColor()) {
    method = "TrueColor (RGB)";
    rgb = {
      r: colorObj.red(),
      g: colorObj.green(),
      b: colorObj.blue(),
    };
  } else {
    method = "Unknown";
  }

  return { method, colorIndex, rgb };
}

/**
 * Get color information from an OdCmColor object and delete it.
 * Convenience function that handles cleanup.
 * @param colorObj The ODA color object (will be deleted)
 * @returns Color information including method and value
 */
export function getColorInfoAndDelete(
  colorObj: DrawingModule.OdCmColor,
): ColorInfo {
  const info = getColorInfo(colorObj);
  deleteObj(colorObj);
  return info;
}

/**
 * An entity's own explicit color, as plain serializable data — safe to store
 * in command params replayed by undo/redo. Exactly one of the two fields is
 * set.
 */
export type ExplicitColorValue =
  | { colorIndex: number; rgb?: never }
  | { rgb: { r: number; g: number; b: number }; colorIndex?: never };

/**
 * Extract an entity's own color as plain data, or `undefined` when the color
 * is inherited (ByLayer/ByBlock) — i.e. there is nothing entity-specific to
 * transfer to another entity.
 * @param colorObj The ODA color object (will be deleted)
 */
export function getExplicitColorValue(
  colorObj: DrawingModule.OdCmColor,
): ExplicitColorValue | undefined {
  const info = getColorInfoAndDelete(colorObj);
  if (info.colorIndex != null) return { colorIndex: info.colorIndex };
  if (info.rgb) return { rgb: info.rgb };
  return undefined;
}

/**
 * Build an OdCmColor from an ExplicitColorValue.
 * @returns The ODA color object — caller must deleteObj() it.
 */
export function buildOdCmColor(
  module: DrawingModuleType,
  value: ExplicitColorValue,
): DrawingModule.OdCmColor {
  const colorObj = new module.OdCmColor();
  if (value.colorIndex != null) {
    colorObj.setColorIndex(value.colorIndex);
  } else if (value.rgb) {
    colorObj.setRGB(value.rgb.r, value.rgb.g, value.rgb.b);
  }
  return colorObj;
}

/**
 * Couleur ACI n° 7 : blanche sur fond sombre, noire sur fond clair. La
 * palette du fond la résout, ici comme dans AutoCAD.
 */
export const ACI_FOREGROUND = 7;

/** Noir ou blanc purs : les couleurs qui disparaîtraient sur l'un des fonds. */
export function isForegroundRgb(rgb: { r: number; g: number; b: number }) {
  const { r, g, b } = rgb;
  return (
    (r === 0 && g === 0 && b === 0) || (r === 255 && g === 255 && b === 255)
  );
}

/**
 * Écrit une couleur RGB dans un OdCmColor. Le noir et le blanc purs passent en
 * ACI 7 plutôt qu'en RGB fixe : ils suivent le fond du plan (sombre ou clair)
 * au lieu de devenir invisibles quand il change.
 */
export function setOdColorRgb(
  colorObj: DrawingModule.OdCmColor,
  rgb: { r: number; g: number; b: number },
): void {
  if (isForegroundRgb(rgb)) colorObj.setColorIndex(ACI_FOREGROUND);
  else colorObj.setRGB(rgb.r, rgb.g, rgb.b);
}

/**
 * Palette ACI standard (sous-ensemble courant : 1-9 + gris). Repli `undefined`
 * pour les autres index — rare dans les légendes DR (couleurs stockées en RGB).
 */
const ACI_RGB: Record<number, [number, number, number]> = {
  1: [255, 0, 0],
  2: [255, 255, 0],
  3: [0, 255, 0],
  4: [0, 255, 255],
  5: [0, 0, 255],
  6: [255, 0, 255],
  7: [255, 255, 255],
  8: [128, 128, 128],
  9: [192, 192, 192],
};

/**
 * Convertit une `ColorInfo` en couleur CSS (`rgb(...)`), ou `undefined` si la
 * couleur n'est pas résolvable seule (ByLayer/ByBlock non résolus, ACI hors palette).
 */
export function colorInfoToCss(
  info: ColorInfo,
  /** Fond du plan (0xRRGGBB) : l'ACI 7 y est noir s'il est clair. */
  backgroundColor?: number,
): string | undefined {
  if (info.rgb) return `rgb(${info.rgb.r}, ${info.rgb.g}, ${info.rgb.b})`;
  if (info.colorIndex != null) {
    if (
      info.colorIndex === ACI_FOREGROUND &&
      backgroundColor !== undefined &&
      !isDarkBackground(backgroundColor)
    ) {
      return "rgb(0, 0, 0)";
    }
    const aci = ACI_RGB[info.colorIndex];
    if (aci) return `rgb(${aci[0]}, ${aci[1]}, ${aci[2]})`;
  }
  return undefined;
}

/**
 * Format color information as a string for logging.
 */
export function formatColorInfo(colorInfo: ColorInfo): string {
  if (colorInfo.rgb) {
    return `${colorInfo.method} - RGB(${colorInfo.rgb.r}, ${colorInfo.rgb.g}, ${colorInfo.rgb.b})`;
  } else if (colorInfo.colorIndex !== undefined) {
    return `${colorInfo.method} - Index ${colorInfo.colorIndex}`;
  } else {
    return colorInfo.method;
  }
}
